import { useState } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import EscudoBadge from "@/components/EscudoBadge";
import FlagRibbon from "@/components/FlagRibbon";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const lang = i18n.language === "en" ? "en" : "pe";
  const prefix = `/${lang}`;

  const nav = [
    { to: `${prefix}`, label: t("nav.home"), end: true, testid: "nav-home" },
    { to: `${prefix}/privacy`, label: t("nav.privacy"), testid: "nav-privacy" },
    { to: `${prefix}/cookies`, label: t("nav.cookies"), testid: "nav-cookies" },
    { to: `${prefix}/terms`, label: t("nav.terms"), testid: "nav-terms" },
  ];

  const switchTo = (target) => {
    if (target === lang) return;
    // Replace language segment in current path
    const rest = location.pathname.replace(/^\/(pe|en)(?=\/|$)/, "") || "/";
    const newPath = `/${target}${rest === "/" ? "" : rest}`;
    i18n.changeLanguage(target);
    navigate(newPath, { replace: false });
    setOpen(false);
  };

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium tracking-wide ${
      isActive
        ? "text-white border-b-2 border-[#10B981]"
        : "text-slate-200 hover:text-white"
    }`;

  return (
    <header
      style={{ backgroundColor: "#0B1D3A" }}
      className="w-full sticky top-0 z-40"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to={prefix}
            className="flex items-center gap-3"
            data-testid="brand-link"
            aria-label="Parakox"
          >
            <span className="rounded-full bg-white p-1.5 shadow-inner">
              <EscudoBadge className="h-9 w-9" />
            </span>
            <span className="flex flex-col leading-tight">
              <span
                className="text-white text-xl"
                style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
              >
                {t("meta.site_name")}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-emerald-300">
                {t("meta.tagline")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-1"
            data-testid="desktop-nav"
          >
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={navLinkClass}
                data-testid={n.testid}
              >
                {n.label}
              </NavLink>
            ))}

            <div
              className="ml-4 flex items-center rounded-full border border-white/25 overflow-hidden"
              role="group"
              aria-label={t("language.switch_label")}
            >
              <button
                type="button"
                onClick={() => switchTo("pe")}
                data-testid="lang-switch-pe"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  lang === "pe"
                    ? "bg-[#10B981] text-white"
                    : "text-slate-200 hover:text-white"
                }`}
              >
                {t("language.short_es")}
              </button>
              <button
                type="button"
                onClick={() => switchTo("en")}
                data-testid="lang-switch-en"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  lang === "en"
                    ? "bg-[#10B981] text-white"
                    : "text-slate-200 hover:text-white"
                }`}
              >
                {t("language.short_en")}
              </button>
            </div>
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-white/10"
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4" data-testid="mobile-nav">
            <div className="flex flex-col gap-1 border-t border-white/10 pt-3">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-sm font-medium ${
                      isActive
                        ? "bg-[#10B981] text-white"
                        : "text-slate-200 hover:bg-white/10 hover:text-white"
                    }`
                  }
                  data-testid={`m-${n.testid}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="mt-2 flex items-center gap-2 px-1">
                <span className="text-xs text-slate-300 uppercase tracking-wider">
                  {t("language.switch_label")}:
                </span>
                <button
                  type="button"
                  onClick={() => switchTo("pe")}
                  data-testid="m-lang-switch-pe"
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                    lang === "pe"
                      ? "bg-[#10B981] text-white"
                      : "border border-white/30 text-slate-200"
                  }`}
                >
                  {t("language.short_es")}
                </button>
                <button
                  type="button"
                  onClick={() => switchTo("en")}
                  data-testid="m-lang-switch-en"
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                    lang === "en"
                      ? "bg-[#10B981] text-white"
                      : "border border-white/30 text-slate-200"
                  }`}
                >
                  {t("language.short_en")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <FlagRibbon />
    </header>
  );
}
